/**
 * TemplatesPage
 *
 * Main container component for the templates feature.
 * Orchestrates the sidebar, editor, and library views with proper state management.
 */

import React, { useCallback, useMemo, useState } from 'react';
import ManageCustomSections from '@components/Shared/ManageCustomSections';
import ModuleSelector from '@components/Templates/ModuleSelector';
import NotePreviewModal from '@components/Templates/PreviewTemplate';
import TemplateSharing from '@components/Templates/TemplateSharing';
import { useDeleteModal } from '@context/modals/DeleteModalContext';
import { Box, Drawer, Modal, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

import { LAYOUT_DIMENSIONS } from './constants/layout';
import { useTemplateEditor } from './hooks/useTemplateEditor';
import { useTemplates } from './hooks/useTemplates';
import {
  TemplateBottomBar,
  TemplateEditor as TemplateEditorComponent,
  TemplateLibraryBrowser,
  TemplatesSidebar,
  TemplateTopBar,
  TemplateWelcomeView,
} from './components';
import type {
  AdminPublicTemplate,
  AvailableModule,
  TemplatesViewMode,
  TemplateWithMeta,
  UserTemplateItem,
} from './types';
import { isUserTemplate } from './types';

export const TemplatesPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { showDeleteModal, hideDeleteModal } = useDeleteModal();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Core templates state
  const {
    templates,
    isLoading,
    selectedTemplate,
    isTemplateLoading,
    availableModules,
    moduleMappings,
    publicTemplates,
    selectTemplate,
    reorderTemplates,
    createFromScratch,
    copyUserTemplate,
    copyPublicTemplate,
    addPublicTemplate,
    deleteTemplate,
    removePublicTemplate,
    refresh,
    canModifyTemplate,
  } = useTemplates();

  // Selected template item reference
  const [selectedTemplateItem, setSelectedTemplateItem] = useState<UserTemplateItem | null>(null);

  // View mode state
  const [viewMode, setViewMode] = useState<TemplatesViewMode>('empty');

  // UI state
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);
  const [isModuleSelectorOpen, setIsModuleSelectorOpen] = useState(false);
  const [addSectionIndex, setAddSectionIndex] = useState<number | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Calculate if user has custom modules
  const hasCustomModules = useMemo(
    () =>
      availableModules?.some(
        module => module.is_custom_user_module && module.deactivated !== true,
      ) || false,
    [availableModules],
  );

  // Template editor state
  const canModify = selectedTemplateItem ? canModifyTemplate(selectedTemplateItem) : false;

  const templateEditor = useTemplateEditor({
    template: selectedTemplate,
    availableModules,
    moduleMappings,
    canModify,
    onSave: refresh,
  });

  // Computed values
  const excludedTemplateIds = useMemo(
    () => templates.map(t => t.template_id),
    [templates],
  );

  const shareLink = useMemo(() => {
    if (!selectedTemplateItem) return '';
    return `${import.meta.env.VITE_MAIN_URL}/share/templates/${selectedTemplateItem.template_id}`;
  }, [selectedTemplateItem]);

  // Handle template selection
  const handleSelectTemplate = useCallback(
    (template: TemplateWithMeta) => {
      setSelectedTemplateItem(template);
      selectTemplate(template);
      setViewMode('editor');

      // Close sidebar on mobile
      if (isMobile) {
        setIsSidebarOpen(false);
      }
    },
    [selectTemplate, isMobile],
  );

  // Handle creating new template from scratch
  const handleCreateFromScratch = useCallback(async () => {
    const templateId = await createFromScratch();
    if (templateId) {
      // Select the new template
      const newTemplate: UserTemplateItem = {
        template_id: templateId,
        type: 'user',
        display_name: '[Name Your Template]',
      };
      setSelectedTemplateItem(newTemplate);
      selectTemplate(newTemplate);
      setViewMode('editor');
    }
  }, [createFromScratch, selectTemplate]);

  // Handle browsing library
  const handleBrowseLibrary = useCallback(() => {
    setSelectedTemplateItem(null);
    selectTemplate(null);
    setViewMode('library');
  }, [selectTemplate]);

  // Handle adding template from library
  const handleAddFromLibrary = useCallback(
    async (templateId: string) => {
      const newTemplateId = await addPublicTemplate(templateId);
      if (newTemplateId) {
        const newTemplate: UserTemplateItem = {
          template_id: newTemplateId,
          type: 'public',
          display_name: publicTemplates.find(t => t.template_id === templateId)?.public_name || '',
        };
        setSelectedTemplateItem(newTemplate);
        selectTemplate(newTemplate);
        setViewMode('editor');
      }
    },
    [addPublicTemplate, selectTemplate, publicTemplates],
  );

  // Handle customizing template from library
  const handleCustomizeFromLibrary = useCallback(
    async (template: AdminPublicTemplate) => {
      const newTemplateId = await copyPublicTemplate(template.template_id);
      if (newTemplateId) {
        const newTemplate: UserTemplateItem = {
          template_id: newTemplateId,
          type: 'user',
          display_name: template.public_name || '',
        };
        setSelectedTemplateItem(newTemplate);
        selectTemplate(newTemplate);
        setViewMode('editor');
      }
    },
    [copyPublicTemplate, selectTemplate],
  );

  // Handle duplicate
  const handleDuplicate = useCallback(async () => {
    if (!selectedTemplateItem || !selectedTemplate) return;

    let newTemplateId: string | null = null;

    if (selectedTemplateItem.type === 'user') {
      newTemplateId = await copyUserTemplate(selectedTemplateItem.template_id);
    } else if (selectedTemplateItem.type === 'public') {
      newTemplateId = await copyPublicTemplate(selectedTemplateItem.template_id);
    }

    if (newTemplateId) {
      const newTemplate: UserTemplateItem = {
        template_id: newTemplateId,
        type: 'user',
        display_name: selectedTemplate.template_name + ' (Copy)',
      };
      setSelectedTemplateItem(newTemplate);
      selectTemplate(newTemplate);
    }
  }, [selectedTemplateItem, selectedTemplate, copyUserTemplate, copyPublicTemplate, selectTemplate]);

  // Handle delete
  const handleDelete = useCallback(() => {
    if (!selectedTemplateItem || !selectedTemplate) return;

    showDeleteModal({
      isOpen: true,
      title: 'Delete Template?',
      children: (
        <Typography>
          Are you sure you want to delete this template? This action cannot be undone.
        </Typography>
      ),
      continueText: 'Delete',
      cancelText: 'Cancel',
      onCancel: hideDeleteModal,
      onContinue: () => {
        void (async () => {
          let success = false;

          if (selectedTemplateItem.type === 'user') {
            success = await deleteTemplate(selectedTemplate.template_id);
          } else if (selectedTemplateItem.type === 'public') {
            success = await removePublicTemplate(selectedTemplate.template_id);
          }

          if (success) {
            setSelectedTemplateItem(null);
            selectTemplate(null);
            setViewMode('empty');
          }

          hideDeleteModal();
        })();
      },
    });
  }, [
    selectedTemplateItem,
    selectedTemplate,
    showDeleteModal,
    hideDeleteModal,
    deleteTemplate,
    removePublicTemplate,
    selectTemplate,
  ]);

  // Handle manage custom sections
  const handleManageCustomSections = useCallback(() => {
    setSelectedTemplateItem(null);
    selectTemplate(null);
    setViewMode('manage-sections');
  }, [selectTemplate]);

  // Handle add section
  const handleAddSectionAt = useCallback((index: number) => {
    setAddSectionIndex(index);
    setIsModuleSelectorOpen(true);
  }, []);

  // Handle modules added from selector
  const handleModulesAdded = useCallback(
    (modules: AvailableModule[]) => {
      templateEditor.addSections(modules, addSectionIndex ?? undefined);
      setIsModuleSelectorOpen(false);
      setAddSectionIndex(null);
    },
    [templateEditor, addSectionIndex],
  );


  // Render main content based on view mode
  const renderMainContent = () => {
    switch (viewMode) {
      case 'library':
        return (
          <TemplateLibraryBrowser
            publicTemplates={publicTemplates}
            excludedTemplateIds={excludedTemplateIds}
            availableModules={availableModules}
            onAddTemplate={handleAddFromLibrary}
            onCustomizeTemplate={handleCustomizeFromLibrary}
            onClose={() => setViewMode('empty')}
          />
        );

      case 'manage-sections':
        return (
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <ManageCustomSections
              availableModules={availableModules?.filter(m => m.is_custom_user_module) || []}
              setAvailableModules={() => refresh()}
            />
          </Box>
        );

      case 'editor':
        return (
          <TemplateEditorComponent
            templateName={templateEditor.templateName}
            sections={templateEditor.sections}
            isLoading={isTemplateLoading}
            canModify={canModify}
            isTitleEditing={templateEditor.isTitleEditing}
            lastSaved={templateEditor.lastSaved}
            publicDescription={templateEditor.publicDescription}
            isAddingSections={isModuleSelectorOpen}
            onStartEditingTitle={templateEditor.startEditingTitle}
            onTitleChange={templateEditor.updateTitle}
            onFinishEditingTitle={templateEditor.finishEditingTitle}
            onReorderSections={templateEditor.reorderSections}
            onRemoveSection={templateEditor.removeSection}
            onRevertCustomization={templateEditor.removeModuleMapping}
            onAddSectionAt={handleAddSectionAt}
          />
        );

      case 'empty':
      default:
        return <TemplateWelcomeView onBrowseLibrary={handleBrowseLibrary} />;
    }
  };

  // Sidebar content
  const sidebarContent = (
    <TemplatesSidebar
      templates={templates}
      selectedTemplateId={selectedTemplateItem?.template_id || null}
      isLoading={isLoading}
      hasCustomModules={hasCustomModules}
      onSelectTemplate={handleSelectTemplate}
      onReorderTemplates={reorderTemplates}
      onCreateFromScratch={handleCreateFromScratch}
      onBrowseLibrary={handleBrowseLibrary}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      {/* Sidebar - Desktop */}
      {!isMobile && (
        <Box
          sx={{
            flexShrink: 0,
            width: LAYOUT_DIMENSIONS.SIDEBAR_WIDTH,
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Sidebar - Mobile Drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          PaperProps={{
            sx: { width: LAYOUT_DIMENSIONS.SIDEBAR_WIDTH },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Top Bar */}
        <TemplateTopBar
          hasSelectedTemplate={viewMode === 'editor' && selectedTemplate !== null}
          isSidebarVisible={isSidebarOpen}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          onPreview={() => setIsPreviewOpen(true)}
          onDuplicate={handleDuplicate}
          onShare={() => setIsShareModalOpen(true)}
        />

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {renderMainContent()}
        </Box>

        {/* Bottom Bar (only for user templates being edited) */}
        {viewMode === 'editor' &&
          selectedTemplateItem?.type === 'user' &&
          isUserTemplate(selectedTemplate) && (
            <TemplateBottomBar
              isSaving={templateEditor.isSaving}
              onSave={templateEditor.save}
              onDelete={handleDelete}
            />
          )}
      </Box>

      {/* Module Selector Modal */}
      {selectedTemplate && (
        <ModuleSelector
          excludedModules={templateEditor.sections.map(s => s.module_id)}
          onAddModules={handleModulesAdded}
          onClose={() => {
            setIsModuleSelectorOpen(false);
            setAddSectionIndex(null);
          }}
          open={isModuleSelectorOpen}
          templateId={selectedTemplate.template_id}
          templateName={templateEditor.templateName}
        />
      )}

      {/* Preview Modal */}
      {selectedTemplate && isPreviewOpen && (
        <NotePreviewModal
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          templateSections={selectedTemplate.sections}
          availableModules={availableModules}
        />
      )}

      {/* Share Modal */}
      <Modal
        open={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        aria-labelledby="share-template-modal"
      >
        <Box>
          <TemplateSharing
            setToastOpen={enqueueSnackbar}
            tempTitle={selectedTemplate?.template_name || ''}
            copyLink={shareLink}
            templateId={selectedTemplate?.template_id}
            setToastMessage={enqueueSnackbar}
            setShowShareModal={setIsShareModalOpen}
          />
        </Box>
      </Modal>
    </Box>
  );
};
